import React, {useEffect, useState} from 'react';
import { UserContainer } from './style';
import { useParams } from 'react-router-dom';
import AuthService from '../../../AuthService';
import { toast } from 'react-toastify';
import { MdPerson, MdEmail, MdPhone, MdLocationOn, MdDescription, MdShoppingCart } from 'react-icons/md';
import { FaDollarSign, FaTruck, FaCalendarAlt, FaFileInvoice } from 'react-icons/fa';

export default function Order() {

    const [order, setOrder] = useState(null);
    const {id} = useParams();
    const [uploadingInvoice, setUploadingInvoice] = useState(false);
    const [showInvoiceUpload, setShowInvoiceUpload] = useState(false);
    const [isProvisional, setIsProvisional] = useState(false);
    const [replaceIndex, setReplaceIndex] = useState(null);

    const getOrder = async () => {
        fetch(`https://api.rb.beeapp.es/admin/orders/${id}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${AuthService.getToken()}`,
            },
        })
            .then(response => response.json())
            .then(data => {
                setOrder(data);
            });
    }

    useEffect(() => {
        getOrder();
    }, [id]);

    const handleGenerateInvoice = async () => {
        setUploadingInvoice(true);

        try {
            const response = await fetch('https://api.rb.beeapp.es/admin/invoice/generate', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${AuthService.getToken()}`,
                },
                body: JSON.stringify({
                    id: id,
                    isProvisional: isProvisional
                })
            });

            const data = await response.json();

            if (response.ok) {
                toast.success('Factura generada correctamente');
                setShowInvoiceUpload(false);
                setIsProvisional(false);
                getOrder();
            } else {
                toast.error(data.message || 'Error al generar la factura');
            }
        } catch (error) {
            console.error(error);
            toast.error('Error al generar la factura');
        } finally {
            setUploadingInvoice(false);
        }
    };

    const handleUploadInvoice = async (file) => {
        if (!file) return;

        setUploadingInvoice(true);
        const formData = new FormData();
        formData.append('invoice', file);
        formData.append('id', id);
        formData.append('isProvisional', isProvisional);
        if (replaceIndex !== null) {
            formData.append('replaceIndex', replaceIndex);
        }

        try {
            const response = await fetch('https://api.rb.beeapp.es/admin/invoice', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${AuthService.getToken()}`,
                },
                body: formData
            });

            const data = await response.json();

            if (response.ok) {
                toast.success(replaceIndex !== null ? 'Factura actualizada correctamente' : 'Factura subida correctamente');
                setShowInvoiceUpload(false);
                setIsProvisional(false);
                setReplaceIndex(null);
                getOrder();
            } else {
                toast.error(data.message || 'Error al subir la factura');
            }
        } catch (error) {
            toast.error('Error al subir la factura');
        } finally {
            setUploadingInvoice(false);
        }
    };

    const updateStatus = async (status) => {
        fetch(`https://api.rb.beeapp.es/admin/orders/${id}/status`, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${AuthService.getToken()}`,
            },
            body: JSON.stringify({
                Status: status,
            }),
        })
            .then(response => response.json())
            .then(data => {
                toast.success('Estado actualizado correctamente');
                getOrder();
            });
    }

    const getStatusBadge = (status) => {
        const statusConfig = {
            'Created': { label: 'Pendiente', bg: '#fef3c7', color: '#92400e' },
            'Preparing': { label: 'Preparando', bg: '#dbeafe', color: '#1e40af' },
            'Shipped': { label: 'Enviado', bg: '#d1fae5', color: '#065f46' },
            'Delivered': { label: 'Entregado', bg: '#d1fae5', color: '#065f46' },
            'Cancelled': { label: 'Cancelado', bg: '#fee2e2', color: '#991b1b' }
        };

        const config = statusConfig[status] || statusConfig['Created'];

        return (
            <span style={{
                backgroundColor: config.bg,
                color: config.color,
                padding: '6px 14px',
                borderRadius: '12px',
                fontSize: '0.875rem',
                fontWeight: '500'
            }}>
                {config.label}
            </span>
        );
    };

    const formatOrderDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-ES', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const formatOrderDateTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-ES', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const printOrder = () => {
        const printWindow = window.open('', '_blank');

        const subtotal = order?.TotalAmount || 0;
        const envio = order?.ShippingCosts || 0;
        const subtotalConEnvio = subtotal + envio;
        const total = subtotalConEnvio * 1.21;
        const iva = total - subtotalConEnvio;

        const htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Pedido #${id?.slice(-8)}</title>
                <style>
                    * {
                        margin: 0;
                        padding: 0;
                        box-sizing: border-box;
                    }
                    body {
                        font-family: Arial, sans-serif;
                        padding: 15px;
                        position: relative;
                        font-size: 14px;
                    }
                    .watermark {
                        position: fixed;
                        top: 50%;
                        left: 50%;
                        transform: translate(-50%, -50%) rotate(-45deg);
                        font-size: 90px;
                        font-weight: bold;
                        color: rgba(220, 220, 220, 0.15);
                        z-index: -1;
                        white-space: nowrap;
                    }
                    .header {
                        background-color: #028322;
                        color: white;
                        padding: 10px 14px;
                        margin-bottom: 14px;
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                    }
                    .header h1 {
                        font-size: 20px;
                        font-weight: bold;
                    }
                    .header-info {
                        text-align: right;
                        font-size: 13px;
                    }
                    .header-info div {
                        margin-bottom: 3px;
                    }
                    .section {
                        margin-bottom: 12px;
                    }
                    .section-title {
                        color: #028322;
                        font-size: 15px;
                        font-weight: bold;
                        margin-bottom: 6px;
                        padding-bottom: 4px;
                        border-bottom: 1px solid #028322;
                    }
                    .info-row {
                        display: flex;
                        margin-bottom: 3px;
                        font-size: 12px;
                        line-height: 1.4;
                    }
                    .info-label {
                        font-weight: bold;
                        width: 90px;
                    }
                    .address-box {
                        background-color: #f9fafb;
                        padding: 8px;
                        border-left: 2px solid #028322;
                        font-size: 12px;
                        line-height: 1.4;
                    }
                    table {
                        width: 100%;
                        border-collapse: collapse;
                        margin-top: 6px;
                        font-size: 11px;
                    }
                    th {
                        background-color: #f3f4f6;
                        padding: 5px 4px;
                        text-align: left;
                        font-weight: bold;
                        border-bottom: 1px solid #e5e7eb;
                        font-size: 11px;
                    }
                    td {
                        padding: 4px;
                        border-bottom: 1px solid #e5e7eb;
                        font-size: 11px;
                    }
                    .totals {
                        margin-top: 12px;
                        text-align: right;
                        font-size: 13px;
                    }
                    .totals-row {
                        display: flex;
                        justify-content: flex-end;
                        margin-bottom: 3px;
                    }
                    .totals-label {
                        width: 150px;
                        text-align: right;
                        padding-right: 12px;
                    }
                    .totals-value {
                        width: 85px;
                        text-align: right;
                        font-weight: bold;
                    }
                    .total-final {
                        font-size: 15px;
                        color: #028322;
                        border-top: 2px solid #028322;
                        padding-top: 5px;
                        margin-top: 5px;
                    }
                    @media print {
                        body {
                            padding: 10px;
                        }
                        .watermark {
                            font-size: 70px;
                        }
                    }
                </style>
            </head>
            <body>
                <div class="watermark">PENDIENTE PAGO</div>

                <div class="header">
                    <h1>PEDIDO #${id?.slice(-8)}</h1>
                    <div class="header-info">
                        <div><strong>Fecha:</strong> ${formatOrderDateTime(order?.CreationDate)}</div>
                        <div><strong>Estado:</strong> ${getStatusBadge(order?.Status).props.children}</div>
                    </div>
                </div>

                <div class="section">
                    <div class="section-title">Información del Cliente</div>
                    <div class="info-row">
                        <div class="info-label">Nombre:</div>
                        <div>${order?.UserID?.Name} ${order?.UserID?.Surname}</div>
                    </div>
                    <div class="info-row">
                        <div class="info-label">Email:</div>
                        <div>${order?.UserID?.Email || '-'}</div>
                    </div>
                    <div class="info-row">
                        <div class="info-label">Teléfono:</div>
                        <div>${order?.UserID?.Phone || '-'}</div>
                    </div>
                    <div class="info-row">
                        <div class="info-label">CIF:</div>
                        <div>${order?.UserID?.CIF || '-'}</div>
                    </div>
                </div>

                ${order?.Address?.length > 0 ? `
                    <div class="section">
                        <div class="section-title">Dirección de Entrega</div>
                        <div class="address-box">
                            ${order.Address[0].Address}<br>
                            ${order.Address[0].City}, ${order.Address[0].ZIP}<br>
                            ${order.Address[0].Country}
                        </div>
                    </div>
                ` : ''}

                <div class="section">
                    <div class="section-title">Productos</div>
                    <table>
                        <thead>
                            <tr>
                                <th style="width: 30px;">✓</th>
                                <th style="width: 100px;">Referencia</th>
                                <th>Producto</th>
                                <th style="width: 80px; text-align: center;">Cantidad</th>
                                <th style="width: 100px; text-align: right;">Precio/ud</th>
                                <th style="width: 100px; text-align: right;">Total</th>
                                <th style="width: 100px;">Estado</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${order?.Products?.map(product => {
                                const productData = product.product || product.productSnapshot;
                                const isDeleted = !product.product && product.productSnapshot;
                                return `
                                <tr${isDeleted ? ' style="background-color: #fef2f2;"' : ''}>
                                    <td><input type="checkbox" /></td>
                                    <td>${productData?.reference || '-'}</td>
                                    <td>${productData?.name || 'Producto descatalogado'}${isDeleted ? ' <span style="color: #dc2626; font-size: 0.75rem;">(Descatalogado)</span>' : ''}</td>
                                    <td style="text-align: center;">${product.quantity}</td>
                                    <td style="text-align: right;">${product.price.toFixed(2)}€</td>
                                    <td style="text-align: right;">${(product.price * product.quantity).toFixed(2)}€</td>
                                    <td>${product.prepared ? 'Listo' : 'Pendiente'}</td>
                                </tr>
                            `}).join('')}
                        </tbody>
                    </table>
                </div>

                <div class="totals">
                    <div class="totals-row">
                        <div class="totals-label">Subtotal:</div>
                        <div class="totals-value">${subtotal.toFixed(2)}€</div>
                    </div>
                    <div class="totals-row">
                        <div class="totals-label">Gastos de envío:</div>
                        <div class="totals-value">${envio.toFixed(2)}€</div>
                    </div>
                    <div class="totals-row">
                        <div class="totals-label">Subtotal con envío:</div>
                        <div class="totals-value">${subtotalConEnvio.toFixed(2)}€</div>
                    </div>
                    <div class="totals-row">
                        <div class="totals-label">IVA (21%):</div>
                        <div class="totals-value">${iva.toFixed(2)}€</div>
                    </div>
                    <div class="totals-row total-final">
                        <div class="totals-label">TOTAL:</div>
                        <div class="totals-value">${total.toFixed(2)}€</div>
                    </div>
                </div>
            </body>
            </html>
        `;

        printWindow.document.write(htmlContent);
        printWindow.document.close();

        printWindow.onload = function() {
            printWindow.focus();
            printWindow.print();
        };
    };

    if (!order) {
        return (
            <UserContainer>
                <div style={{ padding: '40px', textAlign: 'center' }}>
                    <p style={{ color: '#6b7280' }}>Cargando pedido...</p>
                </div>
            </UserContainer>
        );
    }

    return (
        <UserContainer>
            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '24px'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <h1 style={{
                        color: '#028322',
                        fontSize: '1.75rem',
                        fontWeight: '600',
                        margin: 0
                    }}>
                        Pedido #{id?.slice(-8)}
                    </h1>
                    {getStatusBadge(order?.Status)}
                </div>

                <button
                    onClick={printOrder}
                    style={{
                        backgroundColor: '#028322',
                        color: 'white',
                        padding: '10px 20px',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                >
                    📄 Imprimir
                </button>
            </div>

            {/* Cards de información */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px',
                marginBottom: '32px'
            }}>
                <div style={{
                    backgroundColor: '#fff',
                    border: '2px solid #028322',
                    borderRadius: '12px',
                    padding: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                }}>
                    <FaDollarSign size={24} color="#028322" />
                    <div>
                        <p style={{ margin: 0, fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px' }}>
                            SUBTOTAL
                        </p>
                        <p style={{ margin: 0, fontSize: '1.25rem', fontWeight: '700', color: '#1f2937' }}>
                            {order?.TotalAmount?.toFixed(2).replace('.', ',')}€
                        </p>
                    </div>
                </div>

                <div style={{
                    backgroundColor: '#fff',
                    border: '2px solid #f59e0b',
                    borderRadius: '12px',
                    padding: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                }}>
                    <FaTruck size={24} color="#f59e0b" />
                    <div>
                        <p style={{ margin: 0, fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px' }}>
                            ENVÍO
                        </p>
                        <p style={{ margin: 0, fontSize: '1.25rem', fontWeight: '700', color: '#1f2937' }}>
                            {(order?.ShippingCosts || 0).toFixed(2).replace('.', ',')}€
                        </p>
                    </div>
                </div>

                <div style={{
                    backgroundColor: '#fff',
                    border: '2px solid #3b82f6',
                    borderRadius: '12px',
                    padding: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                }}>
                    <FaDollarSign size={24} color="#3b82f6" />
                    <div>
                        <p style={{ margin: 0, fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px' }}>
                            TOTAL
                        </p>
                        <p style={{ margin: 0, fontSize: '1.25rem', fontWeight: '700', color: '#1f2937' }}>
                            {(((order?.TotalAmount || 0) + (order?.ShippingCosts || 0)) * 1.21).toFixed(2).replace('.', ',')}€
                        </p>
                    </div>
                </div>

                <div style={{
                    backgroundColor: '#fff',
                    border: '2px solid #9ca3af',
                    borderRadius: '12px',
                    padding: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                }}>
                    <FaCalendarAlt size={24} color="#9ca3af" />
                    <div>
                        <p style={{ margin: 0, fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px' }}>
                            FECHA
                        </p>
                        <p style={{ margin: 0, fontSize: '1.25rem', fontWeight: '700', color: '#1f2937' }}>
                            {formatOrderDate(order?.CreationDate)}
                        </p>
                    </div>
                </div>
            </div>

            {/* Layout con 2 columnas */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr',
                gap: '24px',
                marginBottom: '32px'
            }}>
                {/* Columna izquierda */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    {/* Información del Cliente */}
                    <div style={{
                        backgroundColor: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '12px',
                        padding: '20px'
                    }}>
                        <h3 style={{
                            margin: 0,
                            marginBottom: '16px',
                            fontSize: '1rem',
                            fontWeight: '600',
                            color: '#1f2937',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <MdPerson size={20} color="#6b7280" />
                            Información del Cliente
                        </h3>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <div>
                                <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: '600', color: '#1f2937' }}>
                                    {order?.UserID?.Name} {order?.UserID?.Surname}
                                </p>
                                <p style={{ margin: 0, fontSize: '0.75rem', color: '#9ca3af' }}>
                                    Cliente
                                </p>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <MdEmail size={16} color="#9ca3af" />
                                <div>
                                    <p style={{ margin: 0, fontSize: '0.75rem', color: '#9ca3af' }}>Email</p>
                                    <p style={{ margin: 0, fontSize: '0.875rem', color: '#1f2937' }}>
                                        {order?.UserID?.Email}
                                    </p>
                                </div>
                            </div>

                            {order?.UserID?.Phone && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <MdPhone size={16} color="#9ca3af" />
                                    <div>
                                        <p style={{ margin: 0, fontSize: '0.75rem', color: '#9ca3af' }}>Teléfono</p>
                                        <p style={{ margin: 0, fontSize: '0.875rem', color: '#1f2937' }}>
                                            {order?.UserID?.Phone}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {order?.UserID?.CIF && (
                                <div>
                                    <p style={{ margin: 0, fontSize: '0.75rem', color: '#9ca3af' }}>CIF</p>
                                    <p style={{ margin: 0, fontSize: '0.875rem', color: '#1f2937' }}>
                                        {order?.UserID?.CIF}
                                    </p>
                                </div>
                            )}

                            {order?.UserID?.EquivalenceSurcharge !== undefined && (
                                <div>
                                    <p style={{ margin: 0, fontSize: '0.75rem', color: '#9ca3af' }}>Recargo Equiv.</p>
                                    <p style={{ margin: 0, fontSize: '0.875rem', color: '#1f2937' }}>
                                        {order?.UserID?.EquivalenceSurcharge ? 'Sí' : 'No'}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Dirección de Entrega */}
                    {order?.Address?.length > 0 && (
                        <div style={{
                            backgroundColor: '#fff',
                            border: '1px solid #e5e7eb',
                            borderRadius: '12px',
                            padding: '20px'
                        }}>
                            <h3 style={{
                                margin: 0,
                                marginBottom: '16px',
                                fontSize: '1rem',
                                fontWeight: '600',
                                color: '#1f2937',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}>
                                <MdLocationOn size={20} color="#10b981" />
                                Dirección de Entrega
                            </h3>

                            {order.Address.map((address, index) => (
                                <div key={index} style={{
                                    padding: '12px',
                                    backgroundColor: '#f9fafb',
                                    borderRadius: '8px',
                                    borderLeft: '4px solid #10b981'
                                }}>
                                    <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: '600', color: '#1f2937' }}>
                                        {address.Address}
                                    </p>
                                    <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>
                                        {address.City}, {address.ZIP}
                                    </p>
                                    <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>
                                        {address.Country}
                                    </p>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Productos */}
                    <div style={{
                        backgroundColor: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '12px',
                        padding: '20px'
                    }}>
                        <h3 style={{
                            margin: 0,
                            marginBottom: '16px',
                            fontSize: '1rem',
                            fontWeight: '600',
                            color: '#1f2937',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <MdShoppingCart size={20} color="#10b981" />
                            Productos ({order?.Products?.length || 0})
                        </h3>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {order?.Products?.map((product, index) => {
                                // Usar productSnapshot si el producto fue eliminado o no existe
                                const productData = product.product || product.productSnapshot;

                                // Si no hay datos del producto, no mostrar
                                if (!productData) return null;

                                const isDeleted = !product.product && product.productSnapshot;

                                return (
                                    <div key={index} style={{
                                        display: 'flex',
                                        gap: '12px',
                                        padding: '12px',
                                        backgroundColor: isDeleted ? '#fef2f2' : '#f9fafb',
                                        borderRadius: '8px',
                                        alignItems: 'center',
                                        border: isDeleted ? '1px solid #fecaca' : 'none'
                                    }}>
                                        <img
                                            src={productData.images && productData.images[0] ? productData.images[0] : 'https://cdn.beeapp.es/distribucionesrb/NoImage.png'}
                                            alt={productData.name || 'Producto'}
                                            style={{
                                                width: '60px',
                                                height: '60px',
                                                objectFit: 'cover',
                                                borderRadius: '6px',
                                                opacity: isDeleted ? 0.7 : 1
                                            }}
                                            onError={(e) => {
                                                e.target.src = 'https://cdn.beeapp.es/distribucionesrb/NoImage.png';
                                            }}
                                        />

                                        <div style={{ flex: 1 }}>
                                            <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: '600', color: isDeleted ? '#991b1b' : '#1f2937' }}>
                                                {productData.name || 'Producto descatalogado'}
                                                {isDeleted && (
                                                    <span style={{
                                                        marginLeft: '8px',
                                                        fontSize: '0.75rem',
                                                        fontWeight: '500',
                                                        color: '#dc2626',
                                                        backgroundColor: '#fee2e2',
                                                        padding: '2px 6px',
                                                        borderRadius: '4px'
                                                    }}>
                                                        Descatalogado
                                                    </span>
                                                )}
                                            </p>
                                            <p style={{ margin: 0, fontSize: '0.75rem', color: '#6b7280' }}>
                                                Ref: {productData.reference || 'N/A'} • {product.quantity} uds • {product.price.toFixed(2)}€
                                            </p>
                                        </div>

                                        <div style={{ textAlign: 'right' }}>
                                            <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: '600', color: '#1f2937' }}>
                                                {(product.price * product.quantity).toFixed(2).replace('.', ',')}€
                                            </p>
                                            <p style={{
                                                margin: 0,
                                                fontSize: '0.75rem',
                                                color: product.prepared ? '#10b981' : '#ef4444',
                                                fontWeight: '500'
                                            }}>
                                                {product.prepared ? '✓ Listo' : '⏳ Pendiente'}
                                            </p>
                                        </div>
                                    </div>
                                );
                            }).filter(item => item !== null)}
                        </div>
                    </div>
                </div>

                {/* Columna derecha */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {/* Acciones */}
                    <div style={{
                        backgroundColor: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '12px',
                        padding: '20px'
                    }}>
                        <h3 style={{
                            margin: 0,
                            marginBottom: '16px',
                            fontSize: '1rem',
                            fontWeight: '600',
                            color: '#1f2937'
                        }}>
                            Acciones
                        </h3>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {/* Botón Marcar como Enviado - solo si no está enviado, entregado o cancelado */}
                            {order?.Status !== 'Shipped' && order?.Status !== 'Delivered' && order?.Status !== 'Cancelled' && (
                                <button
                                    onClick={() => {
                                        if (window.confirm('¿Marcar este pedido como enviado?')) {
                                            updateStatus('Shipped');
                                        }
                                    }}
                                    style={{
                                        width: '100%',
                                        backgroundColor: '#028322',
                                        color: '#fff',
                                        padding: '12px 16px',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontSize: '0.875rem',
                                        fontWeight: '500',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Marcar como Enviado
                                </button>
                            )}

                            {/* Botón Preparando - solo si está en Created o Preparing */}
                            {(order?.Status === 'Created' || order?.Status === 'Preparing') && (
                                <button
                                    onClick={() => {
                                        if (window.confirm('¿Marcar este pedido como preparando?')) {
                                            updateStatus('Preparing');
                                        }
                                    }}
                                    style={{
                                        width: '100%',
                                        backgroundColor: '#fff',
                                        color: '#3b82f6',
                                        padding: '12px 16px',
                                        border: '2px solid #3b82f6',
                                        borderRadius: '8px',
                                        fontSize: '0.875rem',
                                        fontWeight: '500',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Preparando
                                </button>
                            )}

                            {/* Botón Cancelar - solo si no está ya cancelado */}
                            {order?.Status !== 'Cancelled' && (
                                <button
                                    onClick={() => {
                                        if (window.confirm('¿Estás seguro de cancelar este pedido?')) {
                                            updateStatus('Cancelled');
                                        }
                                    }}
                                    style={{
                                        width: '100%',
                                        backgroundColor: '#fff',
                                        color: '#ef4444',
                                        padding: '12px 16px',
                                        border: '2px solid #ef4444',
                                        borderRadius: '8px',
                                        fontSize: '0.875rem',
                                        fontWeight: '500',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancelar Pedido
                                </button>
                            )}

                            {/* Mensaje si no hay acciones disponibles */}
                            {(order?.Status === 'Cancelled' || order?.Status === 'Delivered') && (
                                <p style={{
                                    margin: 0,
                                    fontSize: '0.875rem',
                                    color: '#6b7280',
                                    textAlign: 'center',
                                    padding: '12px'
                                }}>
                                    No hay acciones disponibles
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Facturación */}
                    <div style={{
                        backgroundColor: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '12px',
                        padding: '20px'
                    }}>
                        <h3 style={{
                            margin: 0,
                            marginBottom: '16px',
                            fontSize: '1rem',
                            fontWeight: '600',
                            color: '#1f2937'
                        }}>
                            Facturación
                        </h3>

                        {/* Lista de facturas existentes */}
                        {order?.Billings?.length > 0 && order.Billings.map((billing, index) => (
                            <div
                                key={index}
                                style={{
                                    backgroundColor: '#f9fafb',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '8px',
                                    padding: '16px',
                                    marginBottom: '12px'
                                }}
                            >
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: '12px',
                                    flexWrap: 'wrap'
                                }}>
                                    <div style={{ flex: 1, minWidth: '200px' }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            marginBottom: '4px'
                                        }}>
                                            <FaFileInvoice size={16} color="#3b82f6" />
                                            <span style={{
                                                fontSize: '0.875rem',
                                                fontWeight: '500',
                                                color: '#1f2937'
                                            }}>
                                                Factura #{index + 1}
                                            </span>
                                            {billing.isProvisional && (
                                                <span style={{
                                                    backgroundColor: '#fef3c7',
                                                    color: '#92400e',
                                                    padding: '2px 8px',
                                                    borderRadius: '6px',
                                                    fontSize: '0.75rem',
                                                    fontWeight: '500'
                                                }}>
                                                    PROVISIONAL
                                                </span>
                                            )}
                                        </div>
                                        <p style={{
                                            margin: 0,
                                            fontSize: '0.75rem',
                                            color: '#6b7280'
                                        }}>
                                            {new Date(billing.BillDate).toLocaleDateString('es-ES', {
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric'
                                            })}
                                        </p>
                                    </div>

                                    <div style={{
                                        display: 'flex',
                                        gap: '8px'
                                    }}>
                                        <button
                                            onClick={() => window.open(billing.BillURL)}
                                            style={{
                                                backgroundColor: '#3b82f6',
                                                color: '#fff',
                                                padding: '8px 16px',
                                                border: 'none',
                                                borderRadius: '6px',
                                                fontSize: '0.875rem',
                                                fontWeight: '500',
                                                cursor: 'pointer',
                                                transition: 'background-color 0.2s'
                                            }}
                                            onMouseOver={(e) => e.target.style.backgroundColor = '#2563eb'}
                                            onMouseOut={(e) => e.target.style.backgroundColor = '#3b82f6'}
                                        >
                                            Ver
                                        </button>
                                        <button
                                            onClick={() => {
                                                setReplaceIndex(index);
                                                setShowInvoiceUpload(true);
                                            }}
                                            style={{
                                                backgroundColor: '#fff',
                                                color: '#6b7280',
                                                padding: '8px 16px',
                                                border: '1px solid #d1d5db',
                                                borderRadius: '6px',
                                                fontSize: '0.875rem',
                                                fontWeight: '500',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s'
                                            }}
                                            onMouseOver={(e) => {
                                                e.target.style.borderColor = '#9ca3af';
                                                e.target.style.color = '#1f2937';
                                            }}
                                            onMouseOut={(e) => {
                                                e.target.style.borderColor = '#d1d5db';
                                                e.target.style.color = '#6b7280';
                                            }}
                                        >
                                            Reemplazar
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}

                        {(!order?.Billings || order.Billings.length === 0) && (
                            <p style={{
                                margin: 0,
                                marginBottom: '16px',
                                fontSize: '0.875rem',
                                color: '#6b7280',
                                textAlign: 'center',
                                padding: '12px',
                                backgroundColor: '#f9fafb',
                                borderRadius: '8px'
                            }}>
                                No hay facturas disponibles
                            </p>
                        )}

                        {/* Formulario de generación/subida */}
                        {showInvoiceUpload && (
                            <div style={{
                                backgroundColor: '#f0f9ff',
                                border: '1px solid #bfdbfe',
                                borderRadius: '8px',
                                padding: '16px',
                                marginBottom: '16px'
                            }}>
                                <h4 style={{
                                    margin: '0 0 12px 0',
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    color: '#1f2937'
                                }}>
                                    {replaceIndex !== null ? `Reemplazar factura #${replaceIndex + 1}` : 'Nueva factura'}
                                </h4>

                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    marginBottom: '12px'
                                }}>
                                    <input
                                        type="checkbox"
                                        id="provisional-checkbox"
                                        checked={isProvisional}
                                        onChange={(e) => setIsProvisional(e.target.checked)}
                                        style={{
                                            width: '16px',
                                            height: '16px',
                                            cursor: 'pointer'
                                        }}
                                    />
                                    <label
                                        htmlFor="provisional-checkbox"
                                        style={{
                                            fontSize: '0.875rem',
                                            color: '#374151',
                                            cursor: 'pointer',
                                            userSelect: 'none'
                                        }}
                                    >
                                        Marcar como factura provisional
                                    </label>
                                </div>

                                <input
                                    type="file"
                                    accept=".pdf"
                                    onChange={(e) => {
                                        if (e.target.files && e.target.files[0]) {
                                            handleUploadInvoice(e.target.files[0]);
                                        }
                                    }}
                                    style={{
                                        width: '100%',
                                        padding: '8px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '6px',
                                        fontSize: '0.875rem',
                                        marginBottom: '12px',
                                        backgroundColor: '#fff'
                                    }}
                                    disabled={uploadingInvoice}
                                />

                                <button
                                    onClick={() => {
                                        setShowInvoiceUpload(false);
                                        setIsProvisional(false);
                                        setReplaceIndex(null);
                                    }}
                                    style={{
                                        width: '100%',
                                        padding: '8px',
                                        backgroundColor: '#fff',
                                        color: '#6b7280',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '6px',
                                        fontSize: '0.875rem',
                                        fontWeight: '500',
                                        cursor: 'pointer'
                                    }}
                                    disabled={uploadingInvoice}
                                >
                                    Cancelar
                                </button>
                            </div>
                        )}

                        {/* Botones de acción */}
                        {!showInvoiceUpload && (
                            <div style={{
                                display: 'flex',
                                gap: '8px',
                                marginTop: '16px'
                            }}>
                                <button
                                    onClick={() => {
                                        setReplaceIndex(null);
                                        setIsProvisional(false);
                                        setShowInvoiceUpload(true);
                                    }}
                                    style={{
                                        flex: 1,
                                        backgroundColor: '#fff',
                                        color: '#028322',
                                        padding: '12px 16px',
                                        border: '2px solid #028322',
                                        borderRadius: '8px',
                                        fontSize: '0.875rem',
                                        fontWeight: '500',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s'
                                    }}
                                    onMouseOver={(e) => {
                                        e.target.style.backgroundColor = '#f0fdf4';
                                    }}
                                    onMouseOut={(e) => {
                                        e.target.style.backgroundColor = '#fff';
                                    }}
                                    disabled={uploadingInvoice}
                                >
                                    Subir factura
                                </button>
                                <button
                                    onClick={() => {
                                        setReplaceIndex(null);
                                        if (window.confirm('¿Deseas marcar la factura como provisional?')) {
                                            setIsProvisional(true);
                                            handleGenerateInvoice();
                                        } else {
                                            setIsProvisional(false);
                                            handleGenerateInvoice();
                                        }
                                    }}
                                    style={{
                                        flex: 1,
                                        backgroundColor: '#028322',
                                        color: '#fff',
                                        padding: '12px 16px',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontSize: '0.875rem',
                                        fontWeight: '500',
                                        cursor: 'pointer',
                                        transition: 'background-color 0.2s'
                                    }}
                                    onMouseOver={(e) => e.target.style.backgroundColor = '#026a1b'}
                                    onMouseOut={(e) => e.target.style.backgroundColor = '#028322'}
                                    disabled={uploadingInvoice}
                                >
                                    {uploadingInvoice ? 'Generando...' : 'Generar factura automáticamente'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </UserContainer>
    );
}
